import * as Yup from 'yup';

import { ResultViewModel } from './common/ResultViewModel';
import { TableViewModel } from './common/TableViewModel';
import { CustomerViewModel } from './Customer';
import { EstimateAndQuoteDetailViewModel } from './EstimateAndQuoteDetail';

export enum EstimateStatus {
  Draft = 1,
  Approve = 2,
  EmailSent = 3,
  Invoiced = 4,
  Voided = 5,
}

export const estimateAndQuoteValidation = Yup.object().shape({
  customerID: Yup.number()
    .required('Customer field is Required')
    .min(1, 'Customer field is Required'),
  contactID: Yup.number().required('Contact field is Required'),
  shippingAddressID: Yup.number().required(
    'Shipping Address field is Required'
  ),
  billingAddressID: Yup.number().required('Billing Address field is Required'),
  estimateNumber: Yup.string()
    .max(20, 'Estimate Number exceeds the maximum length of 20 characters')
    .nullable(),
  customerRef: Yup.string().max(
    20,
    'Customer Ref exceeds the maximum length of 20 characters'
  ),
  notes: Yup.string()
    .max(200, 'Notes exceeds the maximum length of 200 characters')
    .nullable(),
  discountType: Yup.number().required('Discount Type field is required'),
  discount: Yup.number()
    .min(0, 'Discount must be non-negative')
    .when('discountType', {
      is: 2, // Percentage
      then: (schema) =>
        schema.max(100, 'Percentage discount cannot exceed 100%'),
      otherwise: (schema) => schema,
    }),
  date: Yup.date().required('Date field is required'),
  dueDate: Yup.date()
    .when(
      'date',
      (date, Yup) => date && Yup.min(date, 'Due date must be after date')
    )
    .required('Due Date field is required'),
});

export interface EstimateAndQuoteViewModel {
  id: number;
  estimateNumber: string;
  customerRef: string;
  date: string;
  dueDate: string;
  notes: string;
  discountType?: number; // 1 for Amount, 2 for Percentage
  discount: number;
  status: EstimateStatus;
  customer?: CustomerViewModel;
  customerID?: number;
  contactID: number;
  contactEmail: string;
  contactPhone: string;
  isSameShipping: boolean;
  shippingAddressID: number;
  billingAddressID: number;
  isTaxExempt: boolean;
  isActive: true;
  estimateDetails: EstimateAndQuoteDetailViewModel[];
}

export interface EstimateAndQuoteVwViewModel {
  id: number;
  estimateNumber: string;
  status: EstimateStatus;
  statusDisplayString?: string;
  customerID: number;
  customerCompanyName: string;
  subTotal?: number;
  taxAmount?: number;
  total?: number;
}

export interface EstimateAndQuoteTableResultViewModel
  extends TableViewModel,
    ResultViewModel {
  data: EstimateAndQuoteVwViewModel[];
}

export interface EstimateAndQuoteResultViewModel extends ResultViewModel {
  data: EstimateAndQuoteViewModel;
}

export interface ConvertToInvoiceViewModel {
  newInvoiceId: number;
}

export interface ConvertToInvoiceResultViewModel extends ResultViewModel {
  data: ConvertToInvoiceViewModel;
}

// Estimates and Quotes Default Values
export const estimateAndQuoteDefaultValue: EstimateAndQuoteViewModel = {
  id: 0,
  estimateNumber: '',
  customerRef: '',
  date: '',
  dueDate: '',
  notes: '',
  discountType: 1, // Default to Amount
  discount: 0,
  status: EstimateStatus.Draft,
  customer: undefined,
  customerID: 0,
  contactID: 0,
  contactEmail: '',
  contactPhone: '',
  isSameShipping: false,
  shippingAddressID: 0,
  billingAddressID: 0,
  isTaxExempt: false,
  isActive: true,
  estimateDetails: [],
};

export const estimateAndQuoteTableDefaultValue: EstimateAndQuoteTableResultViewModel =
  {
    data: [],
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
    isSuccess: true,
    errorMessages: [],
  };

export const getStatusText = (statusValue: EstimateStatus): string => {
  const statusOption = estimateStatusSelectOptions.find(
    (option) => option.value === statusValue.toString()
  );
  return statusOption ? statusOption.text : '';
};

export const estimateStatusSelectOptions = [
  {
    value: EstimateStatus.Draft.toString(),
    text: 'Draft',
  },
  {
    value: EstimateStatus.Approve.toString(),
    text: 'Approve',
  },
  {
    value: EstimateStatus.EmailSent.toString(),
    text: 'Email Sent',
  },
  {
    value: EstimateStatus.Invoiced.toString(),
    text: 'Invoiced',
  },
  {
    value: EstimateStatus.Voided.toString(),
    text: 'Voided',
  },
];
